import { useState } from 'react';
import Axios from 'axios';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import PmsUrlConfig from '../../urlConfig';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate } from "react-router-dom";
import AddressSettings from './AddressSettings';
import { getPractice, logout, setAuthToken } from '../common';

export default function UpdateAddressSettings() {
    const practiceDetails = useSelector(state => state.practiceDetails);
    const dispatch = useDispatch();
    const loginDetails = useSelector(state => state.loginDetails);
    
    const [sameAsBilling, setSameAsBilling] = useState(() => {
        if (practiceDetails?.practice?.billing_address && practiceDetails?.practice?.mailing_address &&
            practiceDetails.practice.billing_address.street1 === practiceDetails.practice.mailing_address.street1 &&
            practiceDetails.practice.billing_address.street2 === practiceDetails.practice.mailing_address.street2 &&
            practiceDetails.practice.billing_address.city === practiceDetails.practice.mailing_address.city &&
            practiceDetails.practice.billing_address.state === practiceDetails.practice.mailing_address.state &&
            practiceDetails.practice.billing_address.zip_code === practiceDetails.practice.mailing_address.zip_code) {
            return true;
        } else {
            return false;
        }
    });

    // Early returns after all hooks are called
    if (!practiceDetails) {
        return <Navigate replace to={`${PmsUrlConfig.LOGIN_URL}`} />;
    }

    const practice = practiceDetails.practice;
    if (!practice) {
        return <Navigate replace to={`${PmsUrlConfig.LOGIN_URL}`} />;
    }
    
    const practiceId = practice._id;
    const token = loginDetails?.jwtToken || "";

    const AddressEditSchema = Yup.object().shape({
        billAddrStreet1: Yup.string().required('Required!'),
        billAddrCity: Yup.string().required('Required!'),
        billAddrState: Yup.string().required('Required!'),
        billAddrZipcode: Yup.string().required('Required!'),
        sameAsBilling: Yup.boolean(),

        mailAddrStreet1: Yup.string()
            .when("sameAsBilling", {
                is: false,
                then: Yup.string().required('Required!'),
            }),

        mailAddrCity: Yup.string()
            .when("sameAsBilling", {
                is: false,
                then: Yup.string().required('Required!'),
            }),
        mailAddrState: Yup.string()
            .when("sameAsBilling", {
                is: false,
                then: Yup.string().required('Required!'),
            }),
        mailAddrZipcode: Yup.string()
            .when("sameAsBilling", {
                is: false,
                then: Yup.string().required('Required!'),
            }),
    })

    const handleSameAsBilling = (e) => {
        const isBilling = e.target.checked;
        setSameAsBilling(isBilling);
    };
    return (
        <div className="py-8 px-4 sm:px-6 lg:px-8">
            <Formik
                    initialValues={{
                        billAddrStreet1: practice.billing_address?.street1 || '',
                        billAddrStreet2: practice.billing_address?.street2 || '',
                        billAddrCity: practice.billing_address?.city || '',
                        billAddrState: practice.billing_address?.state || '',
                        billAddrZipcode: practice.billing_address?.zip_code || '',

                        mailAddrStreet1: practice.mailing_address?.street1 || '',
                        mailAddrStreet2: practice.mailing_address?.street2 || '',
                        mailAddrCity: practice.mailing_address?.city || '',
                        mailAddrState: practice.mailing_address?.state || '',
                        mailAddrZipcode: practice.mailing_address?.zip_code || '',
                    }}
                    validationSchema={AddressEditSchema}
                    onSubmit={(values, { setSubmitting, setFieldValue }) => {
                        if (sameAsBilling) {
                            setFieldValue('mailAddrStreet1', values.billAddrStreet1);
                            setFieldValue('mailAddrStreet2', values.billAddrStreet2);
                            setFieldValue('mailAddrCity', values.billAddrCity);
                            setFieldValue('mailAddrState', values.billAddrState);
                            setFieldValue('mailAddrZipcode', values.billAddrZipcode);
                        }
                        setAuthToken(token);
                        Axios.post(PmsUrlConfig.BOS_EDIT_PRACTICE_URL,
                            {
                                _id: practiceId,
                                practice_name: practice.practice_name,
                                EIN: practice.EIN,
                                practice_email: practice.practice_email,
                                phoneNum: practice.phoneNum,
                                NPI: practice.npi,
                                website: practice.website,
                                billing_address: {
                                    street1: values.billAddrStreet1,
                                    street2: values.billAddrStreet2,
                                    city: values.billAddrCity,
                                    state: values.billAddrState,
                                    zip_code: values.billAddrZipcode,
                                },
                                mailing_address: {
                                    street1: values.mailAddrStreet1,
                                    street2: values.mailAddrStreet2,
                                    city: values.mailAddrCity,
                                    state: values.mailAddrState,
                                    zip_code: values.mailAddrZipcode,
                                }
                            }
                        ).then((response) => {
                            if (response.status === 200) {
                                getPractice(practiceId, dispatch, token);
                                dispatch({
                                    type: 'practiceUpdateSuccess',
                                })
                                setSubmitting(false);
                            }
                            else if (response.status === 400) {
                                console.log("STATUS 400:", response)
                                setSubmitting(false);
                                dispatch({
                                    type: 'practiceUpdateError',
                                    payload: response.data.error_code.description
                                })
                            }
                            else if (response.status === 401 || response.status === 403) {
                                logout();
                            }
                        }).catch((error) => {
                            console.log("ERROR:", error)
                            setSubmitting(false);
                            dispatch({
                                type: 'practiceUpdateError',
                                payload: error.response?.data?.error_code?.description || 'An error occurred'
                            })
                        })
                    }}
                >
                    {({ errors, touched, values, handleChange, setFieldValue, handleBlur, isSubmitting }) => (
                        <Form id="update_address_settings_form" className="space-y-6">
                            {/* Success and Error Messages */}
                            {practiceDetails.successMsg && (
                                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                                    {practiceDetails.successMsg}
                                </div>
                            )}
                            {practiceDetails.errorMsg && (
                                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                                    {practiceDetails.errorMsg}
                                </div>
                            )}

                            {/* Address Settings Component - Now contains the form wrapper */}
                            <AddressSettings
                                errors={errors}
                                touched={touched}
                                values={values}
                                handleBlur={handleBlur}
                                handleChange={handleChange}
                                setFieldValue={setFieldValue}
                                isEditPage={true}
                                sameAsBilling={sameAsBilling}
                                handleSameAsBilling={handleSameAsBilling}
                            />

                            {/* Save Button */}
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
                                >
                                    {isSubmitting ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
        </div>
    );
}

